import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Plus,
  PencilSimple,
  Trash,
  CaretLeft,
  Lock,
  X,
  Users,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  fetchRoles,
  fetchPermissionCatalogue,
  createRole,
  updateRole,
  deleteRole,
  fetchRoleMembers,
  assignRole,
  revokeRole,
  type ChambersRole,
  type PermissionCatalogueItem,
} from '@/lib/rolesApi';
import { fetchUsers, type ApiUser } from '@/lib/usersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

export default function RolesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ChambersRole | null | 'new'>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChambersRole | null>(null);
  const [membersFor, setMembersFor] = useState<ChambersRole | null>(null);

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const catalogueQ = useQuery({ queryKey: ['perm-catalogue'], queryFn: fetchPermissionCatalogue, staleTime: 300_000 });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () => {
      toast.success('Role deleted');
      qc.invalidateQueries({ queryKey: ['roles'] });
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete this role')),
  });

  const roles = rolesQ.data ?? [];
  const systemRoles = roles.filter((r) => r.isSystem);
  const customRoles = roles.filter((r) => !r.isSystem);

  return (
    <div>
      <button
        onClick={() => navigate('/admin/settings')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <CaretLeft className="h-4 w-4" /> Settings
      </button>

      <PageHeader
        title="Roles & Permissions"
        subtitle="Define who can do what in your chambers. System roles are fixed; create custom roles for finance, reception and other staff."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            New role
          </Button>
        }
      />

      {rolesQ.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading roles…</div>
      ) : (
        <div className="space-y-8">
          <RoleGroup
            heading="Custom roles"
            hint="Roles you created for this chambers. Fully editable."
            roles={customRoles}
            emptyLabel="No custom roles yet — create one to grant a tailored set of permissions."
            onEdit={setEditing}
            onDelete={setDeleteTarget}
            onManageMembers={setMembersFor}
          />
          <RoleGroup
            heading="System roles"
            hint="Built-in roles that ship with Jurivoxa. Read-only."
            roles={systemRoles}
            onEdit={setEditing}
            onDelete={setDeleteTarget}
            onManageMembers={setMembersFor}
          />
        </div>
      )}

      {membersFor && (
        <RoleMembersModal
          role={membersFor}
          onClose={() => {
            setMembersFor(null);
            qc.invalidateQueries({ queryKey: ['roles'] });
          }}
        />
      )}

      {editing && (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          catalogue={catalogueQ.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['roles'] });
            setEditing(null);
          }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
            <h3 className="text-base font-semibold text-foreground">Delete “{deleteTarget.name}”?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {deleteTarget.memberCount > 0
                ? `${deleteTarget.memberCount} member${deleteTarget.memberCount === 1 ? '' : 's'} will lose the permissions this role grants.`
                : 'This role has no members.'}{' '}
              This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
                onClick={() => deleteMut.mutate(deleteTarget.id)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? 'Deleting…' : 'Delete role'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleGroup({
  heading,
  hint,
  roles,
  emptyLabel,
  onEdit,
  onDelete,
  onManageMembers,
}: {
  heading: string;
  hint: string;
  roles: ChambersRole[];
  emptyLabel?: string;
  onEdit: (r: ChambersRole) => void;
  onDelete: (r: ChambersRole) => void;
  onManageMembers: (r: ChambersRole) => void;
}) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{heading}</h2>
        <p className="text-xs text-muted-foreground/70">{hint}</p>
      </div>
      {roles.length === 0 ? (
        emptyLabel ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</CardContent></Card>
        ) : null
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {roles.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {r.isSystem ? <Lock className="h-4 w-4 text-muted-foreground" /> : <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />}
                      <span className="truncate font-medium text-foreground">{r.name}</span>
                    </div>
                    {r.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{r.description}</p>}
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{r.memberCount} member{r.memberCount === 1 ? '' : 's'}</span>
                      <span>·</span>
                      <span>{r.key === 'chambers_admin' || r.key === 'super_admin' ? 'All permissions' : `${r.permissionKeys.length} permission${r.permissionKeys.length === 1 ? '' : 's'}`}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => onManageMembers(r)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Manage members"
                    >
                      <Users className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onEdit(r)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title={r.editable ? 'Edit role' : 'View permissions'}
                    >
                      <PencilSimple className="h-4 w-4" />
                    </button>
                    {r.editable && (
                      <button
                        onClick={() => onDelete(r)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-950/40"
                        title="Delete role"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function RoleEditor({
  role,
  catalogue,
  onClose,
  onSaved,
}: {
  role: ChambersRole | null;
  catalogue: PermissionCatalogueItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const readOnly = role ? !role.editable : false;
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permissionKeys ?? []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group the catalogue by resource for a readable matrix.
  const groups = useMemo(() => {
    const m = new Map<string, PermissionCatalogueItem[]>();
    for (const p of catalogue) {
      if (!m.has(p.resource)) m.set(p.resource, []);
      m.get(p.resource)!.push(p);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalogue]);

  const grantsAll = role?.key === 'chambers_admin' || role?.key === 'super_admin';

  const toggle = (key: string) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (readOnly) return onClose();
    if (name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setSaving(true);
    setError(null);
    try {
      const permissionKeys = [...selected];
      if (role) {
        await updateRole(role.id, { name: name.trim(), description: description.trim() || null, permissionKeys });
        toast.success('Role updated');
      } else {
        await createRole({ name: name.trim(), description: description.trim() || undefined, permissionKeys });
        toast.success('Role created');
      }
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save the role'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <ScrollLock />
      <div className="my-8 w-full max-w-2xl rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <span className="text-sm font-semibold text-foreground">
              {role ? (readOnly ? `${role.name} (system role)` : `Edit ${role.name}`) : 'New role'}
            </span>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          {readOnly && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              This is a built-in system role and cannot be changed. To grant a different set of permissions, create a custom role.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Role name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readOnly}
                placeholder="e.g. Finance"
                className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-60"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Description (optional)</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={readOnly}
                placeholder="What this role is for"
                className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-60"
              />
            </label>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">Permissions</div>
            {grantsAll ? (
              <div className="rounded-md border border-brand-300 bg-brand-50 px-3 py-2 text-xs text-brand-900 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-200">
                This role grants <strong>all permissions</strong> in the chambers.
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map(([resource, perms]) => (
                  <div key={resource} className="rounded-lg border border-border">
                    <div className="border-b border-border bg-muted/30 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {resource}
                    </div>
                    <div className="grid gap-x-4 gap-y-1.5 p-3 sm:grid-cols-2">
                      {perms.map((p) => (
                        <label key={p.key} className="flex cursor-pointer items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={selected.has(p.key)}
                            onChange={() => toggle(p.key)}
                            disabled={readOnly}
                            className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-400"
                          />
                          <span className="min-w-0">
                            <span className="text-foreground">{p.action}{p.scope ? ` (${p.scope})` : ''}</span>
                            {p.description && <span className="block text-xs text-muted-foreground">{p.description}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && (
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : role ? 'Save changes' : 'Create role'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleMembersModal({ role, onClose }: { role: ChambersRole; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');

  const usersQ = useQuery({ queryKey: ['chambers-staff'], queryFn: () => fetchUsers({ limit: 200 }), staleTime: 60_000 });
  const membersQ = useQuery({ queryKey: ['role-members', role.id], queryFn: () => fetchRoleMembers(role.id) });

  const memberIds = useMemo(
    () => new Set((membersQ.data ?? []).map((m) => m.id)),
    [membersQ.data],
  );

  const mut = useMutation({
    mutationFn: ({ userId, assigned }: { userId: string; assigned: boolean }) =>
      assigned ? revokeRole(role.id, userId) : assignRole(role.id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role-members', role.id] }),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update membership')),
  });

  const staff = (usersQ.data ?? []).filter((u: ApiUser) => u.memberType !== 'solicitor');
  const filtered = staff.filter((u) => {
    if (!q.trim()) return true;
    return `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <ScrollLock />
      <div className="my-8 w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <span className="text-sm font-semibold text-foreground">Members · {role.name}</span>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search staff…"
            className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
          {role.key === 'chambers_admin' && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
              Assigning this role grants full chambers-admin access. Use sparingly.
            </p>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-2 pb-2">
          {usersQ.isLoading || membersQ.isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No staff found.</div>
          ) : (
            filtered.map((u) => {
              const assigned = memberIds.has(u.id);
              return (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 hover:bg-muted/50"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground">{u.firstName} {u.lastName}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {u.email} · {u.memberType.replace(/_/g, ' ')}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={assigned}
                    disabled={mut.isPending}
                    onChange={() => mut.mutate({ userId: u.id, assigned })}
                    className="h-4 w-4 shrink-0 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-400"
                  />
                </label>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-end border-t border-border px-5 py-3">
          <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
